import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, RefreshCcw, AlertTriangle, Info, AlertCircle, ChevronLeft, ChevronRight, FileText, Download, Smartphone, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { deviceService, Device } from "@/services/deviceService";
import { companyService } from "@/services/companyService";

interface Company {
  _id: string;
  name: string;
}

interface DeviceLog {
  _id: string;
  deviceToken: string;
  deviceName: string;
  companyId: string;
  companyName: string;
  level: 'INFO' | 'WARNING' | 'ERROR';
  category: string;
  message: string;
  data: any;
  clientTimestamp: string;
  appVersion: string;
  platform: string;
  createdAt: string;
  resolved?: boolean;
  resolvedAt?: string;
}

interface LogStats {
  INFO: number;
  WARNING: number;
  ERROR: number;
}

const CATEGORIES = [
  'WEBSOCKET',
  'INTERNET',
  'PLAYBACK',
  'ANNOUNCEMENT',
  'DOWNLOAD',
  'SCHEDULE',
  'SYSTEM',
  'UPDATE',
  'TOKEN',
  'CACHE',
  'DATABASE'
];

export default function DeviceLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [stats, setStats] = useState<LogStats>({ INFO: 0, WARNING: 0, ERROR: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevel, setSelectedLevel] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedDeviceToken, setSelectedDeviceToken] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [syncingDevice, setSyncingDevice] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>("all");
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedResolved, setSelectedResolved] = useState<string>("all");
  const itemsPerPage = 50;

  // URL parametrelerini oku ve filtreleri uygula
  useEffect(() => {
    const deviceTokenParam = searchParams.get('deviceToken');
    const categoryParam = searchParams.get('category');
    const logIdParam = searchParams.get('logId');

    let hasParams = false;

    if (deviceTokenParam) {
      setSelectedDeviceToken(deviceTokenParam);
      hasParams = true;
    }

    if (categoryParam) {
      setSelectedCategory(categoryParam);
      hasParams = true;
    }

    if (logIdParam) {
      setExpandedLog(logIdParam);
      hasParams = true;
      // Log yüklendikten sonra scroll yap
      setTimeout(() => {
        const element = document.getElementById(`log-${logIdParam}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }

    // URL parametrelerini temizle (tek seferlik filtreleme)
    if (hasParams) {
      searchParams.delete('deviceToken');
      searchParams.delete('category');
      searchParams.delete('logId');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [currentPage, selectedLevel, selectedCategory, selectedDeviceToken, selectedResolved]);

  // Firma seçildiğinde o firmaya ait cihazları getir
  useEffect(() => {
    if (selectedCompany && selectedCompany !== 'all') {
      loadDevicesByCompany(selectedCompany);
    } else {
      setDevices([]);
      setSelectedDevice('all');
    }
  }, [selectedCompany]);

  // Firmaları yükle
  const loadCompanies = async () => {
    try {
      const data = await companyService.getAllCompanies();
      if (data) {
        setCompanies(Array.isArray(data) ? data : data.companies || []);
      }
    } catch (error) {
      console.error('Firmalar yüklenemedi:', error);
    }
  };

  // Firmaya ait cihazları yükle
  const loadDevicesByCompany = async (companyId: string) => {
    try {
      setLoadingDevices(true);
      setSelectedDevice('all');
      const data = await deviceService.getAllDevices(1, 1000);
      if (data.devices) {
        // Sadece seçilen firmaya ait cihazları filtrele
        const companyDevices = data.devices.filter((d: Device) => d.company?._id === companyId);
        setDevices(companyDevices);
      }
    } catch (error) {
      console.error('Cihazlar yüklenemedi:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  // Cihazdan log sync tetikle
  const triggerDeviceSync = async (deviceToken: string, deviceName: string) => {
    try {
      setSyncingDevice(deviceToken);
      const response = await api.post(`/device-logs/trigger-sync/${deviceToken}`);

      if (response.data.success) {
        toast.success(`${deviceName} cihazından log sync tetiklendi`);
        // 3 saniye sonra logları yenile
        setTimeout(() => {
          loadLogs();
        }, 3000);
      } else {
        toast.error(response.data.message || "Sync tetiklenemedi");
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "Sync tetikleme hatası";
      toast.error(message);
    } finally {
      setSyncingDevice(null);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', itemsPerPage.toString());
      params.append('offset', ((currentPage - 1) * itemsPerPage).toString());

      if (selectedLevel !== 'all') {
        params.append('level', selectedLevel);
      }
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedDeviceToken !== 'all') {
        params.append('deviceToken', selectedDeviceToken);
      }
      if (selectedResolved !== 'all') {
        params.append('resolved', selectedResolved);
      }

      const response = await api.get(`/device-logs/all?${params.toString()}`);

      if (response.data.success) {
        setLogs(response.data.data);
        setStats(response.data.stats);
        setTotalLogs(response.data.pagination.total);
      }
    } catch (error) {
      toast.error("Loglar yüklenirken hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrelenmiş loglar (arama için)
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logs;

    const search = searchTerm.toLowerCase().trim();
    return logs.filter(log =>
      log.deviceName?.toLowerCase().includes(search) ||
      log.deviceToken?.toLowerCase().includes(search) ||
      log.message?.toLowerCase().includes(search) ||
      log.category?.toLowerCase().includes(search)
    );
  }, [logs, searchTerm]);

  const totalPages = Math.ceil(totalLogs / itemsPerPage);

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'ERROR':
        return <Badge variant="destructive">ERROR</Badge>;
      case 'WARNING':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">WARNING</Badge>;
      default:
        return <Badge variant="secondary">INFO</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      WEBSOCKET: 'bg-purple-100 text-purple-800',
      INTERNET: 'bg-blue-100 text-blue-800',
      PLAYBACK: 'bg-green-100 text-green-800',
      ANNOUNCEMENT: 'bg-rose-100 text-rose-800',
      DOWNLOAD: 'bg-cyan-100 text-cyan-800',
      SCHEDULE: 'bg-orange-100 text-orange-800',
      SYSTEM: 'bg-gray-100 text-gray-800',
      UPDATE: 'bg-indigo-100 text-indigo-800',
      TOKEN: 'bg-pink-100 text-pink-800',
      CACHE: 'bg-teal-100 text-teal-800',
      DATABASE: 'bg-amber-100 text-amber-800'
    };

    return (
      <Badge className={`${colors[category] || 'bg-gray-100 text-gray-800'} hover:${colors[category] || 'bg-gray-100'}`}>
        {category}
      </Badge>
    );
  };

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #7c3aed, #a855f7)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Cihaz İzleme Sistemi
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Device Monitoring Logs
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Tüm cihazlardan gelen sistem loglarını izleyin. WebSocket bağlantıları,
                  internet durumu, playback hataları ve daha fazlasını takip edin.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <FileText className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 bg-white rounded-xl">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">INFO Logları</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.INFO.toLocaleString()}</h3>
                </div>
                <div className="p-3.5 rounded-xl bg-blue-500/10">
                  <Info className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 bg-white rounded-xl">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">WARNING Logları</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.WARNING.toLocaleString()}</h3>
                </div>
                <div className="p-3.5 rounded-xl bg-yellow-500/10">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 bg-white rounded-xl">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">ERROR Logları</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.ERROR.toLocaleString()}</h3>
                </div>
                <div className="p-3.5 rounded-xl bg-red-500/10">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Cihaz, token veya mesaj ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-11 border-slate-200 rounded-lg"
              />
            </div>

            {/* Level Filter */}
            <Select value={selectedLevel} onValueChange={(value) => { setSelectedLevel(value); setCurrentPage(1); }}>
              <SelectTrigger className="w-[150px] h-11 border-slate-200 rounded-lg">
                <SelectValue placeholder="Seviye" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Seviyeler</SelectItem>
                <SelectItem value="INFO">INFO</SelectItem>
                <SelectItem value="WARNING">WARNING</SelectItem>
                <SelectItem value="ERROR">ERROR</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={(value) => { setSelectedCategory(value); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px] h-11 border-slate-200 rounded-lg">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Resolved Filter */}
            <Select value={selectedResolved} onValueChange={(value) => { setSelectedResolved(value); setCurrentPage(1); }}>
              <SelectTrigger className="w-[150px] h-11 border-slate-200 rounded-lg">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="false">Açık Hatalar</SelectItem>
                <SelectItem value="true">Çözülmüş</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh Button */}
            <Button
              onClick={loadLogs}
              variant="outline"
              className="h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>

          {/* Device Sync Section */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="text-sm font-medium text-slate-700">
                <Smartphone className="w-4 h-4 inline-block mr-2" />
                Cihazdan Log Sync:
              </div>

              {/* Company Select */}
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-[200px] h-11 border-slate-200 rounded-lg">
                  <SelectValue placeholder="Firma Seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">-- Firma Seçin --</SelectItem>
                  {companies.map(company => (
                    <SelectItem key={company._id} value={company._id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Device Select */}
              <Select
                value={selectedDevice}
                onValueChange={setSelectedDevice}
                disabled={selectedCompany === 'all' || loadingDevices}
              >
                <SelectTrigger className="w-[250px] h-11 border-slate-200 rounded-lg">
                  <SelectValue placeholder={loadingDevices ? "Yükleniyor..." : "Cihaz Seçin"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">-- Cihaz Seçin --</SelectItem>
                  {devices.map(device => (
                    <SelectItem key={device._id} value={device.token}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${device.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {device.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sync Selected Device Button */}
              <Button
                onClick={() => {
                  if (selectedDevice && selectedDevice !== 'all') {
                    const device = devices.find(d => d.token === selectedDevice);
                    if (device) {
                      triggerDeviceSync(device.token, device.name);
                    }
                  } else {
                    toast.error('Lütfen bir cihaz seçin');
                  }
                }}
                disabled={!selectedDevice || selectedDevice === 'all' || syncingDevice !== null}
                className="h-11 px-5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
              >
                {syncingDevice ? (
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Sync
              </Button>

              {selectedCompany !== 'all' && devices.length === 0 && !loadingDevices && (
                <span className="text-sm text-slate-500">Bu firmaya ait cihaz bulunamadı</span>
              )}
            </div>
          </div>
        </div>

        {/* Active Device Filter Banner */}
        {selectedDeviceToken !== 'all' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-purple-800">
                <span className="font-semibold">Cihaz Filtresi Aktif:</span> {selectedDeviceToken.substring(0, 16)}...
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDeviceToken('all')}
              className="border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              Filtreyi Temizle
            </Button>
          </div>
        )}

        {/* Results Count */}
        <div className="text-sm text-slate-600">
          Toplam <span className="font-semibold text-slate-800">{totalLogs.toLocaleString()}</span> log kaydı
        </div>

        {/* Logs Table */}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[50px]">Seviye</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Firma</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Cihaz Adı</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Token</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Kategori</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Mesaj</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[80px]">Durum</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Tarih</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[100px]">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="py-4"><div className="h-6 bg-slate-200 rounded animate-pulse w-16"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-24"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-24"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-20"></div></TableCell>
                    <TableCell className="py-4"><div className="h-6 bg-slate-200 rounded animate-pulse w-20"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-48"></div></TableCell>
                    <TableCell className="py-4"><div className="h-6 bg-slate-200 rounded animate-pulse w-16"></div></TableCell>
                    <TableCell className="py-4"><div className="h-4 bg-slate-200 rounded animate-pulse w-32"></div></TableCell>
                    <TableCell className="py-4"><div className="h-8 bg-slate-200 rounded animate-pulse w-20"></div></TableCell>
                  </TableRow>
                ))
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Log kaydı bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => {
                  // Firma adı backend'den geliyor
                  const companyName = log.companyName || '-';

                  return (
                  <>
                    <TableRow
                      key={log._id}
                      id={`log-${log._id}`}
                      className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer ${expandedLog === log._id ? 'bg-yellow-50' : ''}`}
                      onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                    >
                      <TableCell className="py-3">
                        {getLevelBadge(log.level)}
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="font-medium text-slate-900">{companyName}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="font-medium text-slate-900">{log.deviceName}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs text-slate-600 font-mono bg-slate-100 px-2 py-1 rounded">{log.deviceToken?.substring(0, 12)}...</span>
                      </TableCell>
                      <TableCell className="py-3">
                        {getCategoryBadge(log.category)}
                      </TableCell>
                      <TableCell className="py-3 max-w-[250px]">
                        <span className="text-slate-700 truncate block">{log.message}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        {log.level === 'ERROR' ? (
                          log.resolved ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Çözüldü
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                              <XCircle className="w-3 h-3 mr-1" />
                              Açık
                            </Badge>
                          )
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 text-slate-600 text-sm whitespace-nowrap">
                        {format(new Date(log.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                      </TableCell>
                      <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => triggerDeviceSync(log.deviceToken, log.deviceName)}
                          disabled={syncingDevice === log.deviceToken}
                          className="h-8 px-3 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
                        >
                          {syncingDevice === log.deviceToken ? (
                            <RefreshCcw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3 mr-1" />
                          )}
                          Sync
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedLog === log._id && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={9} className="py-4 px-6">
                          <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                            {/* Üst Bilgi Kartları */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 font-medium mb-1">Firma</p>
                                <p className="text-sm font-semibold text-slate-900">{companyName}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 font-medium mb-1">Cihaz Adı</p>
                                <p className="text-sm font-semibold text-slate-900">{log.deviceName}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 font-medium mb-1">Token</p>
                                <p className="text-xs font-mono text-slate-700 break-all">{log.deviceToken}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 font-medium mb-1">Uygulama Versiyonu</p>
                                <p className="text-sm font-semibold text-slate-900">{log.appVersion || '-'}</p>
                              </div>
                            </div>

                            {/* Hata Mesajı */}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-xs text-red-600 font-medium mb-1">Hata Mesajı</p>
                              <p className="text-sm text-red-800">{log.message}</p>
                            </div>

                            {/* Ek Veri */}
                            {log.data && Object.keys(log.data).length > 0 && (
                              <div>
                                <p className="text-xs text-slate-500 font-medium mb-2">Detaylı Veri</p>
                                <div className="bg-slate-900 text-green-400 rounded-lg p-3 overflow-x-auto">
                                  <pre className="text-xs font-mono">{JSON.stringify(log.data, null, 2)}</pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">{totalLogs.toLocaleString()}</span> log içinden{" "}
              <span className="font-semibold text-slate-800">{((currentPage - 1) * itemsPerPage + 1).toLocaleString()}</span>-
              <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, totalLogs).toLocaleString()}</span>{" "}
              arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Önceki
              </Button>

              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let startPage = Math.max(1, currentPage - 2);
                  if (currentPage > totalPages - 2) {
                    startPage = Math.max(1, totalPages - 4);
                  }
                  const page = startPage + i;
                  if (page <= totalPages) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={`h-10 min-w-10 px-3 rounded-lg ${
                          currentPage === page
                            ? "bg-purple-600 hover:bg-purple-700 text-white"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </Button>
                    );
                  }
                  return null;
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50"
              >
                Sonraki
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
